import React from 'react';
import { BODY_SHAPES, type BodyShape } from './bodyShapes';

// Front/back body map for the Skill Manager (AXISREF497, restyled AXISREF499).
// Region codes match the body_regions table. The front view shows the patient
// facing the viewer, so the patient's right side is drawn on the viewer's left.

export type BodySide = 'left' | 'right' | 'na';

// Front and back share one coordinate space; each view crops its half.
const VIEW_BOX: Record<'front' | 'back', string> = {
  front: '-4 -3 157 298',
  back: '160 -3 157 298',
};

interface RegionGroup {
  region: string;
  side: BodySide;
  paths: string[];
}

// One button per region and side, however many muscle paths draw it.
function groupsFor(view: 'front' | 'back'): RegionGroup[] {
  const groups = new Map<string, RegionGroup>();
  BODY_SHAPES[view].forEach((shape: BodyShape) => {
    const key = `${shape.region}:${shape.side}`;
    const group = groups.get(key) ?? { region: shape.region, side: shape.side, paths: [] };
    group.paths.push(shape.d);
    groups.set(key, group);
  });
  return [...groups.values()];
}

const GROUPS = { front: groupsFor('front'), back: groupsFor('back') };

interface BodyFigureProps {
  view: 'front' | 'back';
  fillFor: (region: string) => string;
  labelFor: (region: string) => string;
  selectedRegion: string | null;
  selectedSide: BodySide;
  onSelect: (region: string, side: BodySide) => void;
  onHover?: (region: string | null, side: BodySide, event?: React.MouseEvent) => void;
  /** Render as a non-interactive thumbnail (no buttons, not focusable). */
  decorative?: boolean;
}

export const BodyFigure: React.FC<BodyFigureProps> = ({
  view, fillFor, labelFor, selectedRegion, selectedSide, onSelect, onHover, decorative,
}) => {
  const isSelected = (region: string, side: BodySide) =>
    selectedRegion === region && (selectedSide === 'na' || side === 'na' || side === selectedSide);

  return (
    <svg
      className={`body-figure-svg${selectedRegion ? ' has-selection' : ''}`}
      viewBox={VIEW_BOX[view]}
      role={decorative ? 'presentation' : 'group'}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : `Body, ${view} view`}
      data-testid={decorative ? undefined : `body-figure-${view}`}
    >
      {GROUPS[view].map(group => {
        const key = `${group.region}-${group.side}`;
        const paths = group.paths.map((d, i) => <path key={i} d={d} />);
        if (decorative) {
          return (
            <g key={key} className="body-region-static" style={{ fill: fillFor(group.region) }}>
              {paths}
            </g>
          );
        }
        const selected = isSelected(group.region, group.side);
        const sideLabel = group.side === 'na' ? '' : ` (${group.side})`;
        return (
          <g
            key={key}
            className={`body-region${selected ? ' selected' : ''}`}
            data-region={group.region}
            data-side={group.side}
            style={{ fill: fillFor(group.region) }}
            role="button"
            tabIndex={0}
            aria-label={`${labelFor(group.region)}${sideLabel}`}
            aria-pressed={selected}
            onClick={() => onSelect(group.region, group.side)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(group.region, group.side);
              }
            }}
            onMouseMove={e => onHover?.(group.region, group.side, e)}
            onMouseLeave={() => onHover?.(null, 'na')}
          >
            {paths}
          </g>
        );
      })}
    </svg>
  );
};
